<template>
    <div id="custom-multiselect" :class="{ 'show-feedback': isInvalid }">
        <vue-multiselect
            :id="id"
            v-model="model"
            label="label"
            :trackBy="trackBy"
            :options="options"
            :loading="loading"
            :multiple="multiple"
            :disabled="disabled"
            :placeholder="placeholder"
            :limitText="getLimitText"
            :selectLabel="selectLabel"
            :closeOnSelect="closeOnSelect"
            :openDirection="openDirection"
            :deselectLabel="deselectLabel"
            :selectedLabel="selectedLabel"
            :groupValues="groupValues"
            :groupLabel="groupLabel"
            :groupSelect="groupSelect"
            :hideSelected="hideSelectedOptions"
            :selectGroupLabel="selectGroupLabel"
            :deselectGroupLabel="deselectGroupLabel"
            :limit="numberOfSelectedOptionsToDisplay"
            :searchable="searchable"
            :class="{ 'is-valid': isValid, 'is-invalid': isInvalid }"
            :tagPlaceholder="$t('form.multiselect.tagPlaceholder')"
            @input="changedSelectedValue()"
            @open="$emit('click', $event)"
            @search-change="changeSearch"
        >
            <template slot="option" slot-scope="props">
                <slot name="c-option" :options="props" />
            </template>

            <span slot="noResult">
                {{ noElementsFound }}
            </span>
            <template slot="noOptions">
                {{ noOptions }}
            </template>
        </vue-multiselect>
    </div>
</template>

<script>
import i18n from '@/i18n'
import VueMultiselect from 'vue-multiselect'

export default {
    name: 'Multiselect',
    components: {
        VueMultiselect,
    },
    props: {
        id: {
            required: false,
            type: String,
        },
        options: {
            required: true,
            type: Array,
        },
        state: {
            default: null,
        },
        selected: {
            required: true,
            default: null,
        },
        trackBy: {
            required: false,
            type: String,
            default: 'value',
        },
        placeholder: {
            default: i18n.t('form.placeholder.select'),
        },
        loading: Boolean,
        disabled: Boolean,
        groupValues: String,
        groupLabel: String,
        groupSelect: Boolean,
        selectGroupLabel: {
            type: String,
            default: i18n.t('form.multiselect.selectGroupLabel'),
        },
        deselectGroupLabel: {
            type: String,
            default: i18n.t('form.multiselect.deselectGroupLabel'),
        },
        showSelectLabel: {
            type: Boolean,
            default: true,
        },
        showSelectedLabel: {
            type: Boolean,
            default: true,
        },
        showDeselectLabel: {
            type: Boolean,
            default: true,
        },
        multiple: {
            default: true,
        },
        openDirection: {
            type: String,
        },
        numberOfSelectedOptionsToDisplay: {
            type: Number,
            default: 99999,
        },
        hideSelectedOptions: {
            type: Boolean,
            default: true,
        },
        closeOnSelect: {
            type: Boolean,
            default: true,
        },
        noOptions: {
            type: String,
            default: i18n.t('form.placeholder.emptyList'),
        },
        noElementsFound: {
            type: String,
            default: i18n.t('form.multiselect.noElementsFound'),
        },
        limitTextTranslationPath: {
            type: String,
            default: 'form.placeholder.andMore',
        },
        searchable: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            model: null,
        }
    },
    computed: {
        isValid() {
            return this.state && this.state !== null
        },
        isInvalid() {
            return !this.state && this.state !== null
        },
        deselectLabel() {
            return this.showDeselectLabel ? '' : i18n.t('periods.multiselect.deselect')
        },
        selectedLabel() {
            return this.showSelectedLabel ? '' : i18n.t('periods.multiselect.selected')
        },
        selectLabel() {
            return this.showSelectLabel ? '' : i18n.t('periods.multiselect.select')
        },
    },
    watch: {
        selected(value) {
            this.model = value
        },
    },
    methods: {
        getLimitText(count) {
            return i18n.t(this.limitTextTranslationPath, { count })
        },
        changedSelectedValue() {
            this.$emit('update:selected', this.model)
            this.$emit('changeValue', this.model)
        },
        changeSearch(query) {
            this.$emit('changeSearch', query)
        },
    },
}
</script>

<style lang="scss" scope>
@import '@/scss/CustomMultiselect.scss';
</style>

<style>
::v-deep .multiselect__content-wrapper {
    position: absolute !important;
    z-index: 9999 !important;
}

.show-feedback ~ .invalid-feedback {
    display: block !important;
}
</style>
